import React, { cloneElement, createRef } from 'react';

import CustomLinkArrowWidget from '../CustomLinkArrowWidget';
import LinkSegmentWidget from '../LinkSegmentWidget';

import { DefaultLinkPointWidget, PathFinding, PathFindingLinkWidget } from '@projectstorm/react-diagrams';
import * as _ from 'lodash';

import classNames from 'classnames';

export default class LinkWidget extends PathFindingLinkWidget {
	constructor(props) {
		super(props);
		this.refPaths = [];
		this.state = {
			selected: false
		};
		this.pathFinding = new PathFinding(this.props.factory);
	}

  onSelection = selected => this.setState({ selected })

  generateLink(path, id, extras = {}) {
    const ref = createRef();
    this.refPaths.push(ref);
    return (
      <LinkSegmentWidget
          diagramEngine={this.props.diagramEngine}
          extras={extras}
          factory={this.props.diagramEngine.getFactoryForLink(this.props.link)}
          key={`link-${id}`}
          link={this.props.link}
          onSelection={this.onSelection}
          path={String(path).endsWith("0 0, 0 0") ? undefined : path}
          segmentRef={ref}
          selected={this.state.selected}
      />
    );
  }

  registerListeners = () => {
    if(!this.listenerTarget) {
      this.listenerTarget = this.props.link.targetPort?.parent.registerListener({
        selectionChanged: () => this.forceUpdate()
      });
    }
    if(!this.listenerSource) {
      this.listenerSource = this.props.link.sourcePort?.parent.registerListener({
        selectionChanged: () => this.forceUpdate()
      });
    }
  }

  generateArrow = (point, prevPoint) => (
    <CustomLinkArrowWidget
        key={point.getID()}
        point={point}
        prevPoint={prevPoint}
    />
  );

  componentWillUnmount = () => {
    clearTimeout(this.timeout)
  }

	generatePoint = point => (
    <DefaultLinkPointWidget
        color={this.props.link.getOptions().color}
        colorSelected={this.props.link.getOptions().selectedColor}
        key={point.getID()}
        point={point}
    />
  );

  wrappedSuperRender = () => {
    this.refPaths = [];
    //ensure id is present for all points on the path
    const points = this.props.link.getPoints();
    const paths = [];
    // first step: calculate a direct path between the points being linked
    const directPathCoords = this.pathFinding.calculateDirectPath(_.first(points), _.last(points));
    const routingMatrix = this.props.factory.getRoutingMatrix();
    // now we need to extract, from the routing matrix, the very first walkable points
    // so they can be used as origin and destination of the link to be created
    const smartLink = this.pathFinding.calculateLinkStartEndCoords(routingMatrix, directPathCoords);
    if (smartLink) {
      const { start, end, pathToStart, pathToEnd } = smartLink;
      // second step: calculate a path avoiding hitting other elements
      const simplifiedPath = this.pathFinding.calculateDynamicPath(routingMatrix, start, end, pathToStart, pathToEnd)
      const dynamicPath = this.props.factory.generateDynamicPath(simplifiedPath, this.props.link.points[0], this.props.link.points[1])
      paths.push(
      //smooth: boolean, extraProps: any, id: string | number, firstPoint: PointModel, lastPoint: PointModel
      this.generateLink(dynamicPath, '0', {}));
    }
    //render the circles
    for (let i = 1; i < points.length - 1; i++) {
      paths.push(this.generatePoint(points[i]));
    }

    if (this.props.link.getTargetPort() !== null) {
      paths.push(this.generateArrow(points[points.length - 1]));
    } else {
      paths.push(this.generatePoint(points[points.length - 1]));
    }
    return React.createElement(React.Fragment, null, paths);
  }

	generateLinePath = (firstPoint, lastPoint) => `M${firstPoint.getX()},${firstPoint.getY()} L ${lastPoint.getX()},${lastPoint.getY()}`;

  classicRender = () => {
		//ensure id is present for all points on the path
		const points = this.props.link.getPoints();
		const paths = [];
		this.refPaths = [];

		//draw the multiple anchors and complex line instead
		for (let j = 0; j < points.length - 1; j++) {
			paths.push(
				this.generateLink(
					this.generateLinePath(points[j], points[j + 1]),
					{
						'data-linkid': this.props.link.getID(),
						'data-point': j,
						onMouseDown: event => {
							this.addPointToLink(event, j + 1);
						}
					},
					j
				)
			);
		}

		//render the circles
		for (let i = 1; i < points.length - 1; i++) {
			paths.push(this.generatePoint(points[i]));
		}

		if (this.props.link.getTargetPort() !== null) {
			paths.push(this.generateArrow(points[points.length - 1], points[points.length - 2]));
		} else {
			paths.push(this.generatePoint(points[points.length - 1]));
		}

		return <g data-default-link-test={this.props.link.getOptions().testName}>{paths}</g>;
  }

  render = () => {
    let el;
    try {
      el = this.wrappedSuperRender()
    } catch {
      el = this.classicRender()
    }
    this.registerListeners()
    if(this.props.link.points[1].position.x === 0 && this.props.link.points[1].position.y === 0) {
      return null;
    }
    const disconnectNotConnectedLines = () => {
      // catch the bug with not connected links not disappearing
      this.timeout = setTimeout(() => {
        if(!this.props.link.targetPort) {
          this.props.diagramEngine.getModel().removeLink(this.props.link.getID())
          Promise.resolve().then(() => {
            this.forceUpdate()
            this.props.diagramEngine.repaintCanvas()
          })
        }
      }, 100)
    }
    return cloneElement(
      el, { 
        className: classNames("insight-link-widget", { "insight-link-widget--is-selected": this.props.link.sourcePort?.parent.options.selected ||  this.props.link.targetPort?.parent.options.selected }) ,
        onMouseUp: disconnectNotConnectedLines,
        onTouchEnd: disconnectNotConnectedLines
      });
  }
}
